import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, Link, Outlet } from "react-router-dom";
import { BackToTop, useRevealAnimations } from "./components/shared";
import { logoWhite } from "./data/content";
import HomePage from "./pages/HomePage";
import "./App.css";

const AboutPage = lazy(() => import("./pages/AboutPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ServiceDetailPage = lazy(() => import("./pages/ServiceDetailPage"));
const SectorsPage = lazy(() => import("./pages/SectorsPage"));
const ExpertisePage = lazy(() => import("./pages/ExpertisePage"));
const LeadershipPage = lazy(() => import("./pages/LeadershipPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const InsightsListingPage = lazy(() => import("./pages/InsightsListingPage"));
const InsightArticlePage = lazy(() => import("./pages/InsightArticlePage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const BoardAdvisoryPage = lazy(() => import("./pages/board/BoardAdvisoryPage"));
const BoardTermsPage = lazy(() => import("./pages/board/BoardTermsPage"));
const BoardConfirmedPage = lazy(() => import("./pages/board/BoardConfirmedPage"));
const AdminLogin = lazy(() => import("./admin/AdminLogin"));
const AdminLayout = lazy(() => import("./admin/AdminLayout"));
const ArticlesList = lazy(() => import("./admin/ArticlesList"));
const ArticleEditor = lazy(() => import("./admin/ArticleEditor"));
const AuthorsPage = lazy(() => import("./admin/AuthorsPage"));
const CategoriesPage = lazy(() => import("./admin/CategoriesPage"));
const MediaLibrary = lazy(() => import("./admin/MediaLibrary"));
const BoardApplications = lazy(() => import("./admin/BoardApplications"));

const ScrollManager = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const target = document.getElementById(id);
        if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [location.pathname, location.hash]);
  return null;
};

const SiteNavbar = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const [logoError, setLogoError] = useState(false);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenMobileDropdown(null);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const toggleMobileDropdown = (key) => {
    setOpenMobileDropdown((prev) => (prev === key ? null : key));
  };

  const navItems = [
    {
      label: "Services", path: "/services",
      dropdown: [
        { label: "Executive Search", path: "/services/executive-search" },
        { label: "AXIS Mscore\u2122", path: "/services/axis-mscore" },
        { label: "AXIS MarketGrid\u2122", path: "/services/axis-marketgrid" },
      ],
    },
    {
      label: "Sectors", path: "/sectors",
      dropdown: [
        { label: "Semiconductor & Advanced Manufacturing", path: "/sectors#semiconductor" },
        { label: "Oil & Gas", path: "/sectors#oil-gas" },
        { label: "Renewables", path: "/sectors#renewables" },
        { label: "Automotive & Aviation", path: "/sectors#automotive-aviation" },
        { label: "Industrials", path: "/sectors#industrial" },
        { label: "Life Sciences & Healthcare Manufacturing", path: "/sectors#life-sciences" },
      ],
    },
    {
      label: "Expertise", path: "/expertise",
      dropdown: [
        { label: "Value Chain Practice", path: "/expertise#value-chain" },
        { label: "Finance & Corporate Practice", path: "/expertise#finance-corporate" },
        { label: "Human Resources Practice", path: "/expertise#human-resources" },
        { label: "Information & Cybersecurity", path: "/expertise#information-cybersecurity" },
      ],
    },
    { label: "Insights", path: "/insights" },
    {
      label: "For Executives", path: "/executives/board-candidacy-advisory",
      dropdown: [
        { label: "Board Candidacy Advisory", path: "/executives/board-candidacy-advisory" },
        { label: "Open Jobs", href: "https://www.linkedin.com/company/axentiaglobal/jobs/?viewAsMember=true", external: true },
      ],
    },
    { label: "About", path: "/about" },
    { label: "Contact", path: "/contact" },
  ];

  return (
    <>
    <header
      className={`site-navbar ${isScrolled ? "scrolled" : ""}`}
      data-testid="site-navbar"
    >
      <div className="shell navbar-shell" data-testid="navbar-shell">
        <Link to="/" className="brand" data-testid="brand-home-link">
          {!logoError ? (
            <img
              src={logoWhite}
              alt="Axentia Global"
              className="brand-logo"
              data-testid="axentia-logo"
              onError={() => setLogoError(true)}
            />
          ) : (
            <span className="brand-text" data-testid="axentia-logo-fallback-text">
              AXENTIA GLOBAL
            </span>
          )}
        </Link>

        <button
          type="button"
          className={`mobile-menu-toggle ${mobileMenuOpen ? "open" : ""}`}
          onClick={() => setMobileMenuOpen((o) => !o)}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileMenuOpen}
          aria-controls="site-nav-menu"
          data-testid="mobile-menu-toggle-button"
        >
          <span className="hamburger-line" />
          <span className="hamburger-line" />
          <span className="hamburger-line" />
        </button>

        <nav
          id="site-nav-menu"
          className={`nav-links ${mobileMenuOpen ? "open" : ""}`}
          data-testid="site-navigation-links"
        >
          {navItems.map((item, index) => (
            <div
              key={item.label}
              className={`nav-item${item.dropdown ? " has-dropdown" : ""}${openMobileDropdown === item.label ? " open" : ""}`}
              data-testid={`nav-item-${item.label.toLowerCase()}`}
            >
              {item.dropdown ? (
                <>
                  <div className="nav-link-row">
                    <Link
                      to={item.path}
                      className={`nav-link ${isActive(item.path) ? "active" : ""}`}
                      data-testid={`nav-link-${index + 1}-${item.label.toLowerCase()}`}
                    >
                      {item.label}
                    </Link>
                    <button
                      type="button"
                      className="nav-sub-toggle"
                      aria-label={openMobileDropdown === item.label ? `Collapse ${item.label} submenu` : `Expand ${item.label} submenu`}
                      aria-expanded={openMobileDropdown === item.label}
                      onClick={() => toggleMobileDropdown(item.label)}
                      data-testid={`nav-sub-toggle-${item.label.toLowerCase()}`}
                    />
                  </div>
                  <div className="nav-dropdown">
                    {item.dropdown.map((sub) =>
                      sub.external ? (
                        <a key={sub.label} href={sub.href} target="_blank" rel="noopener noreferrer">{sub.label}</a>
                      ) : (
                        <Link key={sub.path} to={sub.path}>{sub.label}</Link>
                      )
                    )}
                  </div>
                </>
              ) : (
                <Link
                  to={item.path}
                  className={`nav-link ${isActive(item.path) ? "active" : ""}`}
                  data-testid={`nav-link-${index + 1}-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
        </nav>
      </div>
    </header>
    {mobileMenuOpen && (
      <div
        className="mobile-menu-backdrop"
        onClick={() => setMobileMenuOpen(false)}
        data-testid="mobile-menu-backdrop"
      />
    )}
    </>
  );
};

const SiteFooter = () => {
  const [logoError, setLogoError] = useState(false);

  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="shell footer-grid" data-testid="footer-grid">
        <div className="footer-brand" data-testid="footer-brand-column">
          {!logoError ? (
            <img
              src={logoWhite}
              alt="Axentia Global"
              className="brand-logo"
              data-testid="footer-axentia-logo"
              onError={() => setLogoError(true)}
            />
          ) : (
            <span className="brand-text" data-testid="footer-axentia-logo-fallback-text">
              AXENTIA GLOBAL
            </span>
          )}
          <p data-testid="footer-brand-tagline">
            Executive search and leadership advisory for energy,
            semiconductor, and industrial organisations across Asia.
          </p>
        </div>

        <div className="footer-col" data-testid="footer-services-column">
          <h4 data-testid="footer-services-title">Services</h4>
          <Link to="/services/executive-search" data-testid="footer-link-executive-search">
            Executive Search
          </Link>
          <Link to="/services/axis-mscore" data-testid="footer-link-axis-mscore">
            AXIS Mscore™
          </Link>
          <Link to="/services/axis-marketgrid" data-testid="footer-link-axis-marketgrid">
            AXIS MarketGrid™
          </Link>
        </div>

        <div className="footer-col" data-testid="footer-executives-column">
          <h4 data-testid="footer-executives-title">For Executives</h4>
          <Link to="/executives/board-candidacy-advisory" data-testid="footer-link-board-candidacy-advisory">
            Board Candidacy Advisory
          </Link>
          <a
            href="https://www.linkedin.com/company/axentiaglobal/jobs/?viewAsMember=true"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-link-open-jobs"
          >
            Open Jobs
          </a>
        </div>

        <div className="footer-col" data-testid="footer-company-column">
          <h4 data-testid="footer-company-title">Company</h4>
          <Link to="/about" data-testid="footer-link-about">About</Link>
          <Link to="/expertise" data-testid="footer-link-expertise">Expertise</Link>
          <Link to="/sectors" data-testid="footer-link-sectors">Sectors</Link>
          <Link to="/insights" data-testid="footer-link-insights">Insights</Link>
        </div>
      </div>

      <div className="shell footer-bottom" data-testid="footer-bottom-bar">
        <p data-testid="footer-copyright">&copy; 2026 Axentia Global. All rights reserved.</p>
        <div className="footer-bottom-links" data-testid="footer-legal-links">
          <Link to="/privacy-policy" data-testid="footer-privacy-policy-link">Privacy Policy</Link>
          <Link to="/terms-of-service" data-testid="footer-terms-of-service-link">Terms of Service</Link>
        </div>
      </div>
    </footer>
  );
};

const MainLayout = () => {
  const location = useLocation();
  useRevealAnimations(location.pathname);

  return (
    <div className="axentia-site" data-testid="axentia-layout">
      <div className="grain-overlay" />
      <div className="content-layer">
        <SiteNavbar />
        <div key={location.pathname} className="page-transition">
          <Outlet />
        </div>
        <SiteFooter />
        <BackToTop />
      </div>
    </div>
  );
};

const termsParagraphs = [
  "Welcome to the Axentia Global website. By accessing and using this website, you agree to be bound by these Terms of Service. If you do not agree with these terms, please do not use this website.",
  "Use of Website: This website is provided for general informational purposes regarding Axentia Global's executive search and leadership advisory services across energy and industrial sectors in Asia. The content is intended for business professionals and potential clients seeking information about our services. You agree to use this website responsibly and in compliance with all applicable laws.",
  "Intellectual Property: All content on this website, including but not limited to text, graphics, logos, images, and the compilation thereof, is the property of Axentia Global and is protected by intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from any content without our prior written consent.",
  "No Warranties: The information provided on this website is presented 'as is' without any warranties, express or implied. While we endeavour to keep the information up-to-date and accurate, we make no representations or warranties of any kind about the completeness, accuracy, reliability, or suitability of the information. Any reliance you place on such information is strictly at your own risk.",
  "Limitation of Liability: To the fullest extent permitted by applicable law, Axentia Global shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising out of or related to your use of, or inability to use, this website or any content therein.",
  "Third-Party Links: This website may contain links to third-party websites. These links are provided for your convenience only. We have no control over the content of those sites and accept no responsibility for them or for any loss or damage that may arise from your use of them.",
  "Engagement Terms: Any engagement discussions, mandates, and advisory work are governed by separate formal agreements between Axentia Global and its clients. The information on this website does not constitute an offer or contract.",
  "Governing Law: These Terms of Service shall be governed by and construed in accordance with the laws of the Republic of Singapore. For matters involving our operations in India, Indian law shall apply to the extent relevant. Any disputes arising from or relating to these terms shall be subject to the exclusive jurisdiction of the courts of Singapore.",
  "Changes to Terms: We reserve the right to modify these Terms of Service at any time. Any changes will be effective immediately upon posting on this website. Your continued use of the website following the posting of revised terms constitutes your acceptance of such changes.",
  "Contact: If you have any questions regarding these Terms of Service, please contact us at contact@axentiaglobal.com. Our registered office is located at 5th Floor, Punj Essen House, 17\u201318 Nehru Place, New Delhi, Delhi 110019, India. We also operate in Singapore and across Asia-Pacific.",
];

export default function App() {
  return (
    <BrowserRouter>
      <ScrollManager />
      <Suspense fallback={null}>
      <Routes>
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<ArticlesList />} />
          <Route path="articles/new" element={<ArticleEditor />} />
          <Route path="articles/:id/edit" element={<ArticleEditor />} />
          <Route path="authors" element={<AuthorsPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="media" element={<MediaLibrary />} />
          <Route path="board" element={<BoardApplications />} />
        </Route>

        {/* Public Routes */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:slug" element={<ServiceDetailPage />} />
          <Route path="/sectors" element={<SectorsPage />} />
          <Route path="/expertise" element={<ExpertisePage />} />
          <Route path="/leadership" element={<LeadershipPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/insights" element={<InsightsListingPage />} />
          <Route path="/insights/:slug" element={<InsightArticlePage />} />
          <Route path="/executives/board-candidacy-advisory" element={<BoardAdvisoryPage />} />
          <Route path="/executives/board-candidacy-advisory/terms" element={<BoardTermsPage />} />
          <Route path="/executives/board-candidacy-advisory/confirmed" element={<BoardConfirmedPage />} />
          <Route
            path="/privacy-policy"
            element={<PrivacyPolicyPage />}
          />
          <Route
            path="/terms-of-service"
            element={
              <LegalPage
                pageLabel="Terms of Service"
                pageTitle="Terms of Service"
                metaDescription="The terms governing use of the Axentia Global website, including intellectual property, limitation of liability and governing law in Singapore."
                bodyParagraphs={termsParagraphs}
                testIdPrefix="terms-of-service"
              />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
